// SPDX-FileCopyrightText: 2026 Igor Brkic <igor@hyperglitch.com>
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * Rectangles and three-valued containment.
 *
 * This is the geometry that used to serve regions. The shapes are the same; what
 * changed is what they mean. A region was a promise about the whole board — every
 * difference had to be inside it or the merge was refused. A rectangle promises
 * nothing: it is a gesture that gathers up changes, and drawing one says only
 * "these are the changes I mean".
 *
 * `straddling` therefore stops being an error condition and becomes a question
 * the user answers. It is still never resolved silently — a change half inside a
 * rectangle is reported and left switched off until someone says otherwise.
 */

import { contains, intersects, type BBox, type MaybeBBox } from '../model/bbox.ts';

export interface Rect {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

export type Containment = 'inside' | 'outside' | 'straddling' | 'unknown';

/** Float slack, in mm. 1 nm — far below any meaningful PCB dimension. */
export const CONTAINMENT_TOLERANCE_MM = 1e-6;

/** Build a rectangle from any two opposite corners. */
export function makeRect(minX: number, minY: number, maxX: number, maxY: number): Rect {
  return {
    minX: Math.min(minX, maxX),
    minY: Math.min(minY, maxY),
    maxX: Math.max(minX, maxX),
    maxY: Math.max(minY, maxY),
  };
}

export function rectArea(r: Rect): number {
  return (r.maxX - r.minX) * (r.maxY - r.minY);
}

export function rectBox(r: Rect): BBox {
  return { minX: r.minX, minY: r.minY, maxX: r.maxX, maxY: r.maxY };
}

/**
 * Classify a box against a rectangle.
 *
 * `null` — an object whose extent could not be measured — is `unknown` rather
 * than outside. Not knowing where something is has never been evidence that it
 * is somewhere harmless.
 */
export function classifyBox(box: MaybeBBox, rect: Rect, tol = CONTAINMENT_TOLERANCE_MM): Containment {
  if (box === null) return 'unknown';
  if (contains(rect, box, tol)) return 'inside';
  if (!intersects(rect, box, -tol)) return 'outside';
  return 'straddling';
}
